<template>
  <div class="dashboard">
    <div v-if="loading" class="loading"><div class="spinner"></div></div>
    <div v-else-if="error" class="error">
      <p>{{ error }}</p>
      <button @click="fetchData" class="retry-button">Retry</button>
    </div>
    <div v-else class="dashboard-content">
      <div class="date-controls">
        <label> Von: </label>
        <input
            type="date"
            v-model="startDate"
            :max="endDate"
            :min="minStartDate"
        />
        <label> Bis: </label>
        <input
            type="date"
            v-model="endDate"
            :min="startDate"
            :max="today"
        />
        <button @click="fetchData" class="btn btn-primary">Laden</button>
      </div>
      <div class="chart-container">
        <canvas ref="historyChart"></canvas>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, nextTick, watch } from 'vue';
import Chart from 'chart.js/auto';

export default {
  name: 'History',
  setup() {
    const historyChart = ref(null);
    const trafficData = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const startDate = ref(new Date(Date.now() - 7 * 86400000).toISOString().substr(0, 10)); // 7 days ago
    const endDate = ref(new Date().toISOString().substr(0, 10));
    let chart = null;

    const timeFormat = new Intl.DateTimeFormat('de-DE', {
      timeZone: 'Europe/Berlin',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    const today = new Date().toISOString().substr(0, 10);
    const minStartDate = '2024-01-01'; // or dynamically computed if needed

    const fetchData = async () => {
      loading.value = true;
      error.value = null;
      try {
        const response = await fetch(`/api/traffic?start=${startDate.value}&end=${endDate.value}`);
        if (!response.ok) console.log('Failed to fetch historical data');
        const data = await response.json();
        data.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        trafficData.value = data;

        await nextTick();
        renderChart();
      } catch (err) {
        error.value = err.message;
      } finally {
        loading.value = false;
      }
    };

    const renderChart = () => {
      if (chart) chart.destroy();

      const labels = [];
      const actual = [];
      const measured = [];
      const allTimeActive = 3;
      const removeStationary = (num) => Math.max(num - allTimeActive, 0);

      trafficData.value.forEach((item) => {
        const timeLabel = timeFormat.format(new Date(item.timestamp));
        labels.push(timeLabel);
        actual.push(removeStationary(item.estimated_real_amount));
        measured.push(item.unique_device_count);
      });

      chart = new Chart(historyChart.value, {
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              label: 'Actual',
              data: actual,
              backgroundColor: 'rgba(54, 162, 235, 0.3)',
              borderColor: 'rgba(54, 162, 235, 1)',
              borderWidth: 2,
              tension: 0.4,
              fill: true,
              pointRadius: ctx => ctx.dataIndex === ctx.chart.data.labels.length - 1 ? 4 : 0,
              pointHoverRadius: 5,
            },
            {
              label: 'Measured',
              data: measured,
              backgroundColor: 'rgba(54,235,105,0.3)',
              borderColor: 'rgb(54,235,117)',
              borderWidth: 2,
              tension: 0.4,
              fill: true,
              pointRadius: ctx => ctx.dataIndex === ctx.chart.data.labels.length - 1 ? 4 : 0,
              pointHoverRadius: 5,
              hidden: true,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
              align: 'end',
              labels: {
                boxWidth: 10,
                usePointStyle: true,
                padding: 10,
                font: {
                  size: 9,
                  family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                  weight: '600',
                },
                color: '#D1D5DB',
              },
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              callbacks: {
                label: ctx => {
                  const descriptions = {
                    Measured: "MAC addresses detected in the area.",
                    Actual: "Estimated visitor count from measured data (regression model).",
                  };
                  return `${ctx.dataset.label}: ${ctx.formattedValue} — ${descriptions[ctx.dataset.label] || ''}`;
                },
              },
              titleFont: {
                size: 10,
                weight: '600',
                family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
              },
              bodyFont: {
                size: 9,
                family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
              },
              backgroundColor: 'rgba(0, 0, 0, 0.85)',
              titleColor: '#d1d5db',
              bodyColor: '#e5e7eb',
              borderColor: '#38bdf8',
              borderWidth: 1,
              padding: 8,
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: {
                color: 'rgba(141,188,5,0.15)',
              },
              ticks: {
                font: {
                  size: 9,
                  family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                },
                color: '#d1d5db',
              },
            },
            x: {
              grid: { display: false },
              ticks: {
                maxRotation: 0,
                padding: 5,
                autoSkip: true,
                maxTicksLimit: 6,
                font: {
                  size: 7,
                  family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                },
                color: '#d1d5db',
              },
            },
          },
        },
      });
    };

    onMounted(() => {
      fetchData(); // don't render chart yet
    });

    watch(loading, async (isLoading) => {
      if (!isLoading) {
        await nextTick();
        const canvas = historyChart.value;
        if (canvas && canvas.getContext) {
          renderChart();
        } else {
          console.error('Chart not created: canvas not ready:', canvas);
        }
      }
    });

    return {
      trafficData,
      loading,
      error,
      startDate,
      endDate,
      today,
      minStartDate,
      fetchData,
      historyChart,
    };
  },
};
</script>

<style scoped>
.dashboard {
  padding: 0;
  max-width: 100%;
  margin: 0 auto;
  height: 95vh;
  display: flex;
  flex-direction: column;
  color: #e0e0e0; /* light text */
}

.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  color: #e0e0e0;
}

.spinner {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: #8dbc05; /* corporate green */
  animation: spin 1s infinite linear;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  color: #f87171; /* softer red for error, readable on dark */
}

.retry-button {
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  background-color: #8dbc05;
  color: #121212;
  border: none;
  border-radius: 0.25rem;
  cursor: pointer;
  font-weight: 600;
  transition: background-color 0.3s ease;
}

.retry-button:hover {
  background-color: #81a905;
}

.dashboard-content {
  margin: 20px;
  display: flex;
  background-color: #2a2a2a; /* dark container */
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
  flex-direction: column;
  flex: 1;
  color: #e0e0e0;
}

.btn {
  border-radius: 6px;
  text-decoration: none;
  transition: all 0.3s ease;
  user-select: none;
}

.btn-primary {
  background-color: #8dbc05;
  color: #121212;
}

.date-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 1rem;
}

/* Stack vertically on small screens */
@media (max-width: 600px) {
  .date-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .date-controls > * {
    width: 100%;
  }
}

.chart-container {
  background-color: #2a2a2a; /* match dashboard bg */
  padding: 0.5rem;
  box-shadow: none;
  height: 100%;
  width: auto;
  position: relative;
  flex: 1;
  color: #e0e0e0;
}
</style>

